/**
 * @class UserInputHandler
 * @param {Object} canvasDomElement
 * @param {TraversalState} traversaltState
 * @param {Scene} scene
 * @constructor
 */
export class UserInputHandler {
    /* eslint-enable @typescript-eslint/no-empty-function */
    /**
     * @method constructor
     * @param {Object} canvasDomElement
     * @param {TraversalState} traversaltState
     * @param {Scene} scene
     */
    constructor(canvasDomElement, traversaltState, scene) {
        this._panEnabled = true;
        this._zoomEnabled = true;
        /* eslint-disable @typescript-eslint/no-empty-function */
        this._registeredEvents = {
            onMouseDown: (evt) => { },
            onMouseUp: (evt) => { },
            onMouseWheel: (evt) => { },
            onMouseMove: (evt) => { },
            onMouseOver: (evt) => { },
            onMouseOut: (evt) => { },
            onScroll: (evt) => { },
            onClick: (evt) => { },
            onDoubleClick: (evt) => { },
            onKeyDown: (evt) => { },
            onKeyUp: (evt) => { },
            onResize: (evt) => { },
        };
        this.activePointers = new Map();
        this.lastDistance = null;
        this.canvas = canvasDomElement;
        this.traversalState = traversaltState;
        this.scene = scene;
        //subscribe events
        this.canvas.addEventListener("wheel", (evt) => this.onMouseWheel(evt));
        this.canvas.addEventListener("scroll", (evt) => this.onScroll(evt));
        this.canvas.addEventListener("click", (evt) => this.onClick(evt));
        this.canvas.addEventListener("dblclick", (evt) => this.onDoubleClick(evt));
        this.canvas.addEventListener("keydown", (evt) => this.onKeyDown(evt));
        this.canvas.addEventListener("keyup", (evt) => this.onKeyUp(evt));
        this.canvas.addEventListener("pointerdown", (evt) => this.onPointerDown(evt));
        this.canvas.addEventListener("pointerup", (evt) => this.onPointerUp(evt));
        this.canvas.addEventListener("pointermove", (evt) => this.onPointerMove(evt));
        this.canvas.addEventListener("pointercancel", (evt) => {
            this._releasePointerEvents();
        });
        this.canvas.addEventListener("pointerout", (evt) => {
            this.onPointerOut(evt);
        });
        //disable context menu on canvas
        this.canvas.addEventListener("contextmenu", (evt) => {
            evt.preventDefault();
            evt.stopPropagation();
            return false;
        });
    }
    /**
     * Unregister an event
     * @method unregisterEvent
     * @param {string} eventName
     * @param  eventFunction
     */
    registerEvent(eventName, eventFunction) {
        if (eventName in this._registeredEvents)
            this._registeredEvents[eventName] = eventFunction;
    }
    /**
     * Unregister an event
     * @method unregisterEvent
     * @param {string} eventName
     */
    unregisterEvent(eventName) {
        this._registeredEvents[eventName] = (evt, zR) => {
            return;
        };
    }
    onClick(evt) {
        this.traversalState.mouseClick = true;
        this.traversalState.actualMousePos = [evt.offsetX, evt.offsetY];
        this.traversalState.lockRendering = true;
        this._registeredEvents["onClick"](evt);
        evt.preventDefault();
    }
    onDoubleClick(evt) {
        this._registeredEvents["onDoubleClick"](evt);
        this.traversalState.lockRendering = true;
        evt.preventDefault();
    }
    onPointerDown(evt) {
        evt.preventDefault();
        this.canvas.focus();
        const _evt = new PointerEvent("mousedown", evt);
        this.activePointers.set(evt.pointerId, { x: evt.clientX, y: evt.clientY });
        this.traversalState.mouseDown = true;
        this.traversalState.mouseDownArguments = _evt;
        this.draggingactive = true;
        this.traversalState.actualMousePos = [evt.offsetX, evt.offsetY];
        this.traversalState.lockRendering = true;
        this._registeredEvents["onMouseDown"](evt);
    }
    onPointerUp(evt) {
        evt.preventDefault();
        const _evt = new PointerEvent("mouseup", evt);
        this.activePointers.delete(evt.pointerId);
        this.traversalState.mouseUp = true;
        this.traversalState.mouseUpArguments = _evt;
        this.draggingactive = false;
        this.traversalState.actualMousePos = [evt.offsetX, evt.offsetY];
        this.traversalState.lockRendering = true;
        this._registeredEvents["onMouseUp"](evt);
        this.traversalState.mouseMove = false;
        this.traversalState.mouseMoveArguments = null;
        this.lastDistance = null;
        //The onclick-event is not released from the right mouse button.
        if (evt.button == 2) {
            this._registeredEvents["onClick"](evt);
        }
    }
    _releasePointerEvents() {
        this.activePointers.clear();
        this.traversalState.mouseUp = true;
        this.draggingactive = false;
        this.traversalState.lockRendering = true;
        this.traversalState.mouseMove = false;
        this.traversalState.mouseMoveArguments = null;
        this.lastDistance = null;
    }
    getDistance() {
        if (this.activePointers.size < 2)
            return 0;
        const [p1, p2] = Array.from(this.activePointers.values());
        return Math.hypot(p2.x - p1.x, p2.y - p1.y);
    }
    getMiddlePoint() {
        const pointers = Array.from(this.activePointers.values());
        const p1 = pointers[0], p2 = pointers[1];
        const middlePoint = {
            x: (p1.x + p2.x) / 2,
            y: (p1.y + p2.y) / 2,
        };
        return middlePoint;
    }
    onPointerMove(evt) {
        evt.preventDefault();
        const _evt = new PointerEvent("mousemove", evt);
        if (this.activePointers.size === 0) {
            this.traversalState.mouseMove = true;
            this.traversalState.mouseMoveArguments = _evt;
            this.traversalState.mousePosX = evt.offsetX;
            this.traversalState.mousePosY = evt.offsetY;
            this.traversalState.actualMousePos = [evt.offsetX, evt.offsetY];
            if (this.draggingactive) {
                this.traversalState.pan = true;
                this.traversalState.panArguments = _evt;
            }
            this.traversalState.lockRendering = true;
            this._registeredEvents["onMouseMove"](evt);
        }
        else if (this.activePointers.size === 1 && this._panEnabled) {
            // pan
            this.traversalState.mouseMove = true;
            this.traversalState.mouseMoveArguments = _evt;
            this.traversalState.mousePosX = evt.offsetX;
            this.traversalState.mousePosY = evt.offsetY;
            this.traversalState.actualMousePos = [evt.offsetX, evt.offsetY];
            evt.isDragging = this.draggingactive;
            if (this.draggingactive) {
                this.traversalState.pan = true;
                this.traversalState.panArguments = _evt;
            }
            this.traversalState.lockRendering = true;
            this._registeredEvents["onMouseMove"](evt);
        }
        else if (this.activePointers.size === 2) {
            // pinch zoom
            // set a second active pointer to calculate distance
            this.activePointers.set(evt.pointerId, { x: evt.clientX, y: evt.clientY });
            const newDistance = this.getDistance();
            // set initial distance between fingers
            if (!this.lastDistance) {
                this.lastDistance = newDistance;
            }
            if (this.lastDistance > newDistance) {
                // zoom out
                let deltaY = (this.lastDistance - newDistance) / 2; // Scale factor
                const middlePoint = this.getMiddlePoint();
                const eventCustom = {
                    deltaY: deltaY,
                    target: evt.target,
                    offsetX: middlePoint.x,
                    offsetY: middlePoint.y,
                };
                if (this._zoomEnabled) {
                    this.traversalState.mouseWheel = true;
                    this.traversalState.mouseWheelArguments = eventCustom;
                    this.traversalState.lockRendering = true;
                    this._registeredEvents["onMouseWheel"](eventCustom);
                }
            }
            else {
                // zoom in
                let deltaY = (newDistance - this.lastDistance) / 2; // Scale factor
                const middlePoint = this.getMiddlePoint();
                const eventCustom = {
                    deltaY: -deltaY,
                    target: evt.target,
                    offsetX: middlePoint.x,
                    offsetY: middlePoint.y,
                };
                if (this._zoomEnabled) {
                    this.traversalState.mouseWheel = true;
                    this.traversalState.mouseWheelArguments = eventCustom;
                    this.traversalState.lockRendering = true;
                    this._registeredEvents["onMouseWheel"](eventCustom);
                }
            }
            this.lastDistance = newDistance;
        }
    }
    onPointerOut(evt) {
        evt.preventDefault();
        this._registeredEvents["onMouseOut"](evt);
    }
    onMouseWheel(evt) {
        evt.preventDefault();
        if (!this._zoomEnabled)
            return;
        const event = this._normalizeMouseWheelEvent(evt);
        this.traversalState.mouseWheel = true;
        this.traversalState.mouseWheelArguments = event;
        this.traversalState.lockRendering = true;
        this._registeredEvents["onMouseWheel"](event);
    }
    onResize(evt) {
        this.traversalState.resize = true;
        this.traversalState.resizeArguments = evt;
        this._registeredEvents["onResize"](evt);
    }
    _normalizeMouseWheelEvent(originalEvent) {
        const event = {
            // keep a reference to the original event object
            originalEvent: originalEvent,
            target: originalEvent.target || originalEvent.srcElement,
            type: "wheel",
            deltaMode: originalEvent.type === "MozMousePixelScroll" ? 0 : 1,
            deltaX: 0,
            deltaY: 0,
            offsetX: originalEvent.offsetX,
            offsetY: originalEvent.offsetY,
            preventDefault: function () {
                if (originalEvent.preventDefault) {
                    originalEvent.preventDefault();
                }
                else {
                    originalEvent.returnValue = false;
                }
            },
            stopPropagation: function () {
                if (originalEvent.stopPropagation) {
                    originalEvent.stopPropagation();
                }
                else {
                    originalEvent.cancelBubble = false;
                }
            },
        };
        // calculate deltaY (and deltaX) according to the event
        // 'mousewheel'
        if (originalEvent.deltaY)
            event.deltaY = originalEvent.deltaY / 40;
        // webkit
        if (originalEvent.deltaX)
            event.deltaX = originalEvent.deltaX / 40;
        // 'DomMouseScroll'
        if (originalEvent.detail)
            event.deltaY = originalEvent.detail;
        return event;
    }
    onKeyDown(evt) {
        if ([HTMLInputElement, HTMLTextAreaElement, HTMLSelectElement].some((instance) => evt.target instanceof instance)) {
            return;
        }
        let key = -1;
        if (evt.which == null) {
            key = evt.keyCode; // IE
        }
        else if (evt.which != 0 /*&& evt.charCode!=0*/) {
            key = evt.which; // the rest
        }
        if (key > -1) {
            this.traversalState.keyDown = true;
            this.traversalState.key = key;
            this.traversalState.lockRendering = true;
            this._registeredEvents["onKeyDown"](evt);
        }
    }
    onKeyUp(evt) {
        if ([HTMLInputElement, HTMLTextAreaElement, HTMLSelectElement].some((instance) => evt.target instanceof instance)) {
            return;
        }
        let key = -1;
        if (evt.which == null) {
            key = evt.keyCode; // IE
        }
        else if (evt.which != 0 /*&& evt.charCode!=0*/) {
            key = evt.which; // the rest
        }
        if (key > -1) {
            this.traversalState.keyUp = true;
            this.traversalState.key = key;
            this.traversalState.lockRendering = true;
            this._registeredEvents["onKeyUp"](evt);
        }
    }
    onScroll(evt) {
        this._registeredEvents["onScroll"](evt);
        this.traversalState.lockRendering = true;
        evt.preventDefault();
    }
    set panEnabled(enabled) {
        this._panEnabled = enabled;
    }
    get panEnabled() {
        return this._panEnabled;
    }
    set zoomEnabled(enabled) {
        this._zoomEnabled = enabled;
    }
    get zoomEnabled() {
        return this._zoomEnabled;
    }
}
//# sourceMappingURL=userinputhandler.js.map